
import { GET_USER_DETAILS } from "@/store/modules/auth/constants";
import { AssessmentQuestion } from "@/store/modules/recruiter/interfaces";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

export default Vue.extend({
  name: "ChatInterface",
  data() {
    return {
      newMessage: "",
      isFinalizing: false,
      isStartingNewChat: false,
      imageLoaded: true
    };
  },
  computed: {
    aiIsLoading(): boolean {
      return this.$store.getters["ai_assessment/aiIsLoading"] as boolean;
    },

    aiAssessmentData(): {
      total_questions: number;
      short_answer_count: number;
      mcq_count: number;
      true_false_count: number;
      questions: Array<{ text?: string; [key: string]: unknown }>;
    } | null {
      return this.$store.getters["ai_assessment/aiAssessmentData"] as {
        total_questions: number;
        short_answer_count: number;
        mcq_count: number;
        true_false_count: number;
        questions: Array<{ text?: string; [key: string]: unknown }>;
      } | null;
    },
    aiIsEditMode(): boolean {
      return this.$store.getters["ai_assessment/aiIsEditMode"] as boolean;
    },
    aiLoadingContext(): string | null {
      return this.$store.getters["ai_assessment/aiLoadingContext"] as
        | string
        | null;
    },
    aiChatLog(): Array<{
      type: "ai" | "user" | "system";
      text: string;
      at: number;
    }> {
      return this.$store.getters["ai_assessment/aiChatLog"] as Array<{
        type: "ai" | "user" | "system";
        text: string;
        at: number;
      }>;
    }
  },
  methods: {
    ...mapActions("ai_assessment", [
      "create_session",
      "chat",
      "reset",
      "startEditMode",
      "finishEditMode"
    ]),
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    onImageError() {
      this.imageLoaded = false;
    },
    async initializeSession() {
      try {
        // Only clear chatLog if we're not in edit mode
        if (!this.$route.name?.includes("Edit")) {
          this.$store.commit("ai_assessment/RESET");
        }
        const result = await this.$store.dispatch(
          "ai_assessment/create_session"
        );
        if (result && result.message) {
          this.$store.commit("ai_assessment/APPEND_CHAT", {
            type: "ai",
            text: result.message
          });
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("Failed to create session:", error);
        this.$store.commit("ai_assessment/APPEND_CHAT", {
          type: "ai",
          text: String(
            this.$t(
              "ai-assessments.generated.chat.errors.create_session_failed"
            )
          )
        });
      }
    },
    sendMessage() {
      if (!this.newMessage.trim()) return;

      // Add user message to store
      this.$store.commit("ai_assessment/APPEND_CHAT", {
        type: "user",
        text: this.newMessage
      });

      const userMessage = this.newMessage;
      this.newMessage = "";

      // Check if we're in edit mode
      if (this.aiIsEditMode) {
        this.handleEditModeChat(userMessage);
      } else {
        this.handleNormalChat(userMessage);
      }

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    async handleEditModeChat(userMessage: string) {
      try {
        this.$store.commit("ai_assessment/SET_LOADING", true);

        // Get current session ID
        const sessionId = this.$store.getters["ai_assessment/aiSessionId"];

        if (!sessionId) {
          throw new Error("No session ID available");
        }

        // Send message to AI assistant using recruiter store
        const response = await this.$store.dispatch(
          "recruiter/CHAT_WITH_AI_ASSISTANT",
          {
            session_id: sessionId,
            message: userMessage
          }
        );

        if (response) {
          // Add AI response to chat
          this.$store.commit("ai_assessment/APPEND_CHAT", {
            type: "ai",
            text: response.message
          });
          const updatedAssessmentData = {
            topic: response.assessment_data?.assessment_name || "",
            assessment_type: response.assessment_data?.assessment_type || "",
            difficulty: response.assessment_data?.difficulty || "",
            mcq_count: response.assessment_data.questions.filter(
              (question: AssessmentQuestion) => question.question_type === "mcq"
            ).length,
            true_false_count: response.assessment_data.questions.filter(
              (question: AssessmentQuestion) =>
                question.question_type === "true_false"
            ).length,
            short_answer_count: response.assessment_data.questions.filter(
              (question: AssessmentQuestion) =>
                question.question_type === "short_answer"
            ).length,
            questions: response.assessment_data.questions.map(
              (question: AssessmentQuestion) => {
                return {
                  type: question.question_type,
                  options: question.question_options,
                  correct_answer: question.correct_answer,
                  question: question.question_text
                };
              }
            )
          };

          // Update AI assessment data if provided
          if (updatedAssessmentData) {
            this.$store.commit(
              "ai_assessment/SET_ASSESSMENT_DATA",
              updatedAssessmentData
            );
          }
        }
      } catch (error) {
        console.error("Error in edit mode chat:", error);
        this.$store.commit("ai_assessment/APPEND_CHAT", {
          type: "ai",
          text: String(
            this.$t("ai-assessments.generated.chat.errors.general_error")
          )
        });
      } finally {
        this.$store.commit("ai_assessment/SET_LOADING", false);
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    async handleNormalChat(userMessage: string) {
      // Add empty AI message that will be updated with streaming content
      this.$store.commit("ai_assessment/APPEND_CHAT", {
        type: "ai",
        text: ""
      });

      this.$store.commit("ai_assessment/SET_LOADING", true);

      // Send to AI service chat for streaming response
      this.$store.dispatch("ai_assessment/chat", userMessage).then(() => {
        this.$store.commit("ai_assessment/SET_LOADING", false);
        // The streaming message should already be in the chatLog
        // Just scroll to bottom
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      });
    },
    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages as HTMLElement;
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    },

    async finalizeAssessment() {
      this.isFinalizing = true;
      try {
        // applicant ids is required in the payload
        const applicantIds = this.$store.getters[
          "recruiter/SELECTED_APPLICANTS"
        ].map((applicant: { value: string }) => applicant.value);
        if (applicantIds.length === 0) {
          this.$store.commit(
            "ROOT_NOTIFICATION",
            this.$t("ai-assessments.generated.assign.select_at_least_one")
          );
          return;
        }
        const result = await this.$store.dispatch(
          "ai_assessment/finalize_assessment",
          { applicant_ids: applicantIds }
        );

        if (result.success) {
          // Clear the assessment store data
          this.$store.dispatch("ai_assessment/reset_assessment_data");

          // Navigate to recruiter assessments create page
          this.$router.push("/recruiter/ai-assessments");

          // Show success toast
          this.$store.commit(
            "ROOT_NOTIFICATION",
            this.$t("ai-assessments.generated.chat.created_success")
          );
        } else {
          // Show error toast
          this.$store.commit(
            "ROOT_NOTIFICATION",
            result.error ||
              String(this.$t("ai-assessments.generated.chat.create_failed"))
          );
        }
      } catch (error) {
        console.error("Error finalizing assessment:", error);
        // Show error toast
        this.$store.commit(
          "ROOT_NOTIFICATION",
          this.$t("ai-assessments.generated.chat.create_error")
        );
      } finally {
        this.isFinalizing = false;
      }
    },

    editAssessment() {
      this.$store.dispatch("ai_assessment/startEditMode");
    },

    finishEdit() {
      this.$store.dispatch("ai_assessment/finishEditMode");
    },

    async reinitializeSession() {
      try {
        // Silently reset all data
        this.$store.dispatch("ai_assessment/reset_assessment_data");

        // Create new session without showing page-level loading
        const result = await this.$store.dispatch(
          "ai_assessment/recreate_session"
        );
        if (result && result.message) {
          this.$store.commit("ai_assessment/APPEND_CHAT", {
            type: "ai",
            text: result.message
          });
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("Failed to reinitialize session:", error);
        this.$store.commit("ai_assessment/APPEND_CHAT", {
          type: "ai",
          text: String(
            this.$t("ai-assessments.generated.chat.errors.restart_failed")
          )
        });
      }
    },
    async startNewChat() {
      this.isStartingNewChat = true;
      try {
        await this.reinitializeSession();
      } catch (error) {
        console.error("Error starting new chat:", error);
      } finally {
        this.isStartingNewChat = false;
      }
    }
  },
  mounted() {
    this.initializeSession();
    this.scrollToBottom();
  }
});
